import { type ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../../modules/auth/authStore'
import { Loader } from '../ui/Loader'
import { BrandMark } from '../../components/BrandMark'

interface Props {
  children: ReactNode
}

export function ProtectedRoute({ children }: Props) {
  const { isAuthenticated, isAuthChecked, connectionLost, serverBusy, fetchMe } = useAuthStore()

  useEffect(() => {
    // Проверяем сессию при каждом монтировании (cookie может быть ещё живой)
    void fetchMe()
  }, [fetchMe])

  // Ждём завершения проверки — не редиректим преждевременно при reload страницы
  if (!isAuthChecked) {
    return <Loader variant="brand" fullscreen size={96} label="Загружаем micro·hub" />
  }

  // Сервер не ответил — это не выход: сессия могла остаться живой.
  if (connectionLost) return <ConnectionLostScreen onRetry={fetchMe} busy={serverBusy} />

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function ConnectionLostScreen({
  onRetry,
  busy: serverBusy,
}: {
  onRetry: () => Promise<void>
  busy: boolean
}) {
  const [busy, setBusy] = useState(false)
  const retry = async () => {
    setBusy(true)
    try {
      await onRetry()
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 text-center space-y-4">
        <div className="flex justify-center">
          <BrandMark size={40} />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">
          {serverBusy ? 'Сервер занят' : 'Нет связи с сервером'}
        </h1>
        <p className="text-sm text-gray-500">
          {serverBusy
            ? 'Слишком много запросов за минуту. Подождите минуту и нажмите «Повторить» — пароль вводить заново не придётся.'
            : 'Проверьте интернет или VPN и нажмите «Повторить». Если вход ещё действует, пароль вводить заново не придётся.'}
        </p>
        <button
          type="button"
          onClick={() => void retry()}
          disabled={busy}
          className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-60"
        >
          {busy ? 'Проверяем…' : 'Повторить'}
        </button>
      </div>
    </div>
  )
}
